// Pooling landlord + tenant contact discovery into one view for an owner-occupier lead.
//
// For an OO lead the backend forces landlord == tenant (the same company, mirrored onto
// the landlord decision at promote/save), so `contactCandidates.landlord` and `.tenant`
// are the SAME company ranked under two personas. These pure helpers merge the two blobs
// (deduped by apolloId) so the OO Contacts view shows the union - nobody hidden - while
// the interactive Find/Pull actions still target a single real role (the BE endpoints
// take one role). Merging with a null "other" side is the identity, so the non-OO path
// (which passes poolWithRole=null) stays byte-for-byte unchanged.
//
// Extracted as a shared module (like leadGeo.ts / reviewState.ts) so the ContactsRoleCell
// and its sibling ContactDetailsCell pool over the SAME roles with the SAME logic - a
// person can't show a done-tick in one and be missing from the other (surfaces agree).
import type {
  ContactEnrichmentRoleResult,
  EliminatedPerson,
  EnrichedContact,
  HubSpotRoleResult,
  PeopleCandidate,
  PeopleRoleResult,
} from "@inrange/building-manager-api-client";

// Dedup key: the Apollo id when present, else a name+title fallback (mirrors the React
// key used for apolloId-less legacy eliminated rows, so a person eliminated under both
// personas doesn't list twice).
const eliminatedKey = (p: EliminatedPerson): string =>
  p.apolloId ?? `${p.firstName}-${p.title}`;

const latest = (a: string, b: string): string => (b > a ? b : a);

// Merge two per-role discovery blobs into one. `primary` is the role whose Find/Pull
// actions + trace subjectRef the cell uses, so its scalar/provenance fields (companyName,
// domain, domainSource, attempts, trace ids, promptRevision, sampled) win; the candidate
// and eliminated LISTS are pooled across both.
export const mergePeopleRoleResults = (
  primary: PeopleRoleResult | null,
  other: PeopleRoleResult | null
): PeopleRoleResult | null => {
  if (!other) return primary;
  if (!primary) return other;

  // Candidates: union by apolloId, primary wins on a clash (its persona ranking leads).
  const byId = new Map<string, PeopleCandidate>();
  for (const c of other.candidates) byId.set(c.apolloId, c);
  for (const c of primary.candidates) byId.set(c.apolloId, c);
  const candidates = [...byId.values()];
  const candidateIds = new Set(candidates.map((c) => c.apolloId));

  // Eliminated: union of both, minus anyone kept as a candidate by EITHER persona (a
  // person one ranking kept shouldn't also read as excluded), deduped by key.
  const elimByKey = new Map<string, EliminatedPerson>();
  for (const p of [
    ...(primary.eliminated ?? []),
    ...(other.eliminated ?? []),
  ]) {
    if (p.apolloId && candidateIds.has(p.apolloId)) continue;
    elimByKey.set(eliminatedKey(p), p);
  }

  return {
    ...primary,
    candidates,
    eliminated: [...elimByKey.values()],
    // Same company for an OO lead, so the counts match; take the max defensively.
    apolloTotalEntries: Math.max(
      primary.apolloTotalEntries,
      other.apolloTotalEntries
    ),
    // "complete" if EITHER persona completed, so the union renders even when one side is
    // still just a not_run/throttled marker.
    status:
      primary.status === "complete" || other.status === "complete"
        ? "complete"
        : primary.status,
    discoveredAt: latest(primary.discoveredAt, other.discoveredAt),
  };
};

// Merge two per-role pulled-contact blobs. Union the contacts by apolloId, preferring a
// matched/reveal-bearing record on a clash (and primary on a tie) so the pooled row shows
// the useful reveal.
export const mergeContactEnrichments = (
  primary: ContactEnrichmentRoleResult | null,
  other: ContactEnrichmentRoleResult | null
): ContactEnrichmentRoleResult | null => {
  if (!other) return primary;
  if (!primary) return other;
  const byId = new Map<string, EnrichedContact>();
  for (const c of [...other.contacts, ...primary.contacts]) {
    const existing = byId.get(c.apolloId);
    // Take c when there's nothing yet, when c is a matched reveal, or when the existing
    // one is unmatched (never downgrade a matched record to an unmatched one).
    if (!existing || c.matched || !existing.matched) byId.set(c.apolloId, c);
  }
  return {
    ...primary,
    contacts: [...byId.values()],
    updatedAt: latest(primary.updatedAt, other.updatedAt),
  };
};

// Merge two per-role HubSpot blobs, for the same reason the discovery blobs are merged:
// on an OO lead the tenant cell pools in the landlord persona's people, and those
// apolloIds are matched under the LANDLORD blob. Without pooling here, a pooled-in person
// we already know would render as a stranger in the cell that shows them - the adorner
// disagreeing with the row it sits on.
//
// `primary` wins on the scalars (company, status, readAt, trace id) because they describe
// the role whose Refresh and trace link the cell uses; the per-person maps are unioned,
// primary first.
export const mergeHubSpotRoleResults = (
  primary: HubSpotRoleResult | null,
  other: HubSpotRoleResult | null
): HubSpotRoleResult | null => {
  if (!other) return primary;
  if (!primary) return other;
  const contactsById = new Map(
    [...(other.contacts ?? []), ...(primary.contacts ?? [])].map((c) => [
      c.contactId,
      c,
    ])
  );
  return {
    ...primary,
    contacts: [...contactsById.values()],
    matches: { ...(other.matches ?? {}), ...(primary.matches ?? {}) },
    decisions: { ...(other.decisions ?? {}), ...(primary.decisions ?? {}) },
    promotion: { ...(other.promotion ?? {}), ...(primary.promotion ?? {}) },
  };
};
